// The app's frontend logger, built on LogTape (Python-logging-like: named
// categories, levels, sinks).
//
//   import { getLog } from '../log/logger'
//   const log = getLog('segmentation')
//   log.info('labels applied')
//   log.warn(html`prep <b>${fdi}</b> margin open`, { gapMm: 0.21 })
//   log.error('decode failed', err)
//
// Every record goes to two sinks: the Console's Logs tab (logStore) and the
// browser console. The widget sink never calls console.* itself, so a line
// shows once in DevTools. The exception is origin "window" (uncaught errors,
// see ./globalErrors), which goes to the Logs tab only.
//
// DEBUG is dropped from both sinks while the Logs tab's Debug switch is off.
// Text is taken literally: LogTape's `{name}` placeholders are not used.
import { configureSync, getConsoleSink, getLogger, resetSync, type LogLevel, type LogRecord } from '@logtape/logtape'
import { HtmlMessage } from './html'
import { LOG_LEVEL_LABEL, logStore, type LogLevelName } from './logStore'

/** Every app logger lives under this category, so a third-party library that
 *  also logs through LogTape is never captured by our sinks. */
const ROOT = 'orovia'

export type LogMessage = string | HtmlMessage

export interface Log {
  debug(message: LogMessage, extra?: LogExtra): void
  info(message: LogMessage, extra?: LogExtra): void
  warn(message: LogMessage, extra?: LogExtra): void
  error(message: LogMessage, extra?: LogExtra): void
}

/** Extra data shown under the row's disclosure, or an Error whose stack is. */
export type LogExtra = Record<string, unknown> | Error

function textOf(record: LogRecord): string {
  return record.message.map((part) => (typeof part === 'string' ? part : String(part))).join('')
}

// Property keys getLog uses to carry the html`` markup and the caller's extra
// to the sinks. Both sinks read them and neither passes them on.
const HTML_KEY = '__oroviaHtml'
const EXTRA_KEY = '__oroviaExtra'

interface Extra {
  /** Caller data minus the Error, or undefined when there is none left. */
  properties?: Record<string, unknown>
  error?: Error
}

/** Split the caller's extra into plain data and the (first) Error in it. */
function splitExtra(record: LogRecord): Extra {
  const extra = record.properties[EXTRA_KEY]
  if (extra instanceof Error) return { error: extra }
  if (!extra || typeof extra !== 'object') return {}
  let error: Error | undefined
  const properties: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(extra)) {
    if (!error && value instanceof Error) error = value
    else properties[key] = value
  }
  return {
    ...(Object.keys(properties).length > 0 ? { properties } : {}),
    ...(error ? { error } : {}),
  }
}

function originOf(record: LogRecord): string {
  return record.category.slice(1).join('.')
}

/** LogTape has six levels; the Console shows four. Our facade never emits
 *  trace or fatal, but a raw getLogger() call under the root could. */
function toLevelName(level: LogLevel): LogLevelName {
  if (level === 'trace') return 'debug'
  if (level === 'fatal') return 'error'
  return level
}

/** Reserved origin for uncaught errors and rejections (see ./globalErrors).
 *  Its logger writes to the Logs tab only, so getLog('window') does too. */
export const GLOBAL_ERROR_ORIGIN = 'window'

function widgetSink(record: LogRecord): void {
  const markup = record.properties[HTML_KEY]
  const { properties, error } = splitExtra(record)
  logStore.push({
    timestamp: record.timestamp,
    level: toLevelName(record.level),
    origin: originOf(record),
    text: textOf(record),
    ...(typeof markup === 'string' ? { html: markup } : {}),
    ...(properties ? { properties } : {}),
    ...(error?.stack ? { stack: error.stack } : {}),
  })
}

// DevTools styling, one colour per level, matching the Logs tab.
const LEVEL_CSS: Record<LogLevelName, string> = {
  debug: 'color:#8a8f98',
  info: 'color:inherit',
  warning: 'color:#d99a1e;font-weight:600',
  error: 'color:#e5484d;font-weight:600',
}
const ORIGIN_CSS = 'color:#8a8f98'

/** `%cWARN%c [margin-tool]%c text`, then the data and the Error as live
 *  objects so DevTools can expand them. */
function consoleFormatter(record: LogRecord): readonly unknown[] {
  const { properties, error } = splitExtra(record)
  const args: unknown[] = [
    `%c${LOG_LEVEL_LABEL[toLevelName(record.level)]}%c [${noFormat(originOf(record))}]%c ${noFormat(textOf(record))}`,
    LEVEL_CSS[toLevelName(record.level)],
    ORIGIN_CSS,
    '',
  ]
  if (properties) args.push(properties)
  if (error) args.push(error)
  return args
}

export interface ConfigureLoggingOptions {
  /** Where the DevTools mirror writes. Defaults to the global console. */
  console?: Console
}

export function configureLogging(options: ConfigureLoggingOptions = {}): void {
  configureSync({
    reset: true,
    sinks: {
      widget: widgetSink,
      console: getConsoleSink({ console: options.console, formatter: consoleFormatter }),
    },
    filters: {
      debugGate: (record) => record.level !== 'debug' || logStore.isDebugEnabled(),
    },
    loggers: [
      { category: [ROOT], sinks: ['widget', 'console'], filters: ['debugGate'], lowestLevel: 'debug' },
      // Uncaught errors (./globalErrors): the browser already printed them, so
      // they go to the Logs tab only.
      { category: [ROOT, GLOBAL_ERROR_ORIGIN], sinks: ['widget'], parentSinks: 'override', lowestLevel: 'debug' },
      { category: ['logtape', 'meta'], sinks: ['console'], lowestLevel: 'warning' },
    ],
  })
}

export function resetLogging(): void {
  resetSync()
  logStore.reset()
}

/** LogTape treats `{name}` as a placeholder. Our messages are already final
 *  text, so braces are doubled to come through literally. */
function literal(text: string): string {
  // LogTape only parses (and un-doubles) a template that has a `{` in it, so a
  // text with only `}` must go through as-is or it would come out doubled.
  return text.includes('{') ? text.replace(/[{}]/g, (c) => c + c) : text
}

/** The DevTools line is a format string: a `%` in our text would be read as a
 *  directive (`%c`, `%s`, `%d` …) and eat the style arguments. */
const noFormat = (text: string) => text.replace(/%/g, '%%')

export function getLog(origin: string): Log {
  const logger = getLogger([ROOT, origin])
  const at =
    (method: 'debug' | 'info' | 'warn' | 'error') =>
    (message: LogMessage, extra?: LogExtra): void => {
      const text = typeof message === 'string' ? message : message.text
      const props: Record<string, unknown> = {}
      if (typeof message !== 'string') props[HTML_KEY] = message.html
      if (extra !== undefined) props[EXTRA_KEY] = extra
      logger[method](literal(text), props)
    }
  return { debug: at('debug'), info: at('info'), warn: at('warn'), error: at('error') }
}
